import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Form,
  Button,
  Spin,
  Col,
  Row,
  Typography,
} from 'antd';
import { SearchOutlined, EyeOutlined } from '@ant-design/icons';
import Table from 'components/Table';
import Select from 'components/Select';
import {
  getDeclaracionesEmpresariales,
} from 'api/empresas/declaracionesEmpresarial';
import { getEstatusDeDeclaracionesEmpresariales, getTiposDeDeclaracion } from 'api/empresas';
import { toMoment } from 'utils/normalizers';
import { DATETIME_FRONTEND_FORMAT } from 'utils/formatValues';
import { getPeriodosFiscales } from 'api/catalogos/periodosFiscales';
import { onError } from 'utils/handlers';
import { getDeclaracionPDF } from 'api/recaudacion';
import Previewer from 'components/Previewer';
import { padronesEnum } from 'views/Tramites/GPM/enums';
import { postObtenerReporte } from 'api/reporteador';
import { getTipoDePadronPorContenType } from 'api/catalogos';
import { getClasesDeDeclaracionesEmpresariales } from 'api/empresas/claseDeClaracionesEmpresariales';

export const model = 'declaracionempresarial';
export const catalogsModels = [
  'estatusdedeclaracionempresarial',
  'tipodedeclaracionempresarial',
  'clasededeclaracionempresarial',
  'periodofiscal',
];

export const permissionConfig = {
  title: 'Declaraciones',
  model,
  catalogs: catalogsModels,
};
const DeclaracionesEmpresariales = ({ contribuyente, permission }) => {
  const [form] = Form.useForm();
  const [data, setData] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tiposDeDeclaracion, setTiposDeDeclaracion] = useState([]);
  const [periodosFiscales, setPeriodosFiscales] = useState([]);
  const [estatus, setEstatus] = useState([]);
  const [base64, setBase64] = useState();
  const [visiblePreview, setVisiblePreview] = useState(false);
  const [errorPreviewer, setErrorPreviewer] = useState('');
  const [clasesDeDeclaracionesEmpresariales, setClasesDeDeclaracionesEmpresariales] = useState([]);

  useEffect(() => {
    let mounted = true;
    const fetch = async () => {
      setLoading(true);
      const [
        _tiposDeDeclaraciones,
        _periodosFiscales,
        _estatus,
        _clasesDeDeclaracionesEmpresariales,
      ] = await Promise.all([
        getTiposDeDeclaracion(),
        getPeriodosFiscales(),
        getEstatusDeDeclaracionesEmpresariales(),
        getClasesDeDeclaracionesEmpresariales(),
      ]);
      if (mounted) {
        setTiposDeDeclaracion(_tiposDeDeclaraciones);
        setPeriodosFiscales(_periodosFiscales);
        setEstatus(_estatus);
        setClasesDeDeclaracionesEmpresariales(_clasesDeDeclaracionesEmpresariales);
        setLoading(false);
      }
    };
    fetch();
    return () => { mounted = false; };
  }, []);

  const onClickFormatoDeDeclaracion = async (id, tipoDePadron) => {
    setLoading(true);
    const record = data.find((item) => item.id === id);
    const claseDeDeclaracion = clasesDeDeclaracionesEmpresariales
      .find((clase) => clase.id === record.clase_de_declaracion_empresarial);
    const ID = claseDeDeclaracion?.reporte_formato_de_declaracion_id ?? null;

    const values = {
      ID,
      Parametros: [
        {
          Nombre: tipoDePadron === padronesEnum.AGENCIA ? 'id_declaracion' : 'declaracionID',
          TipoParametro: 'Texto',
          Valor: record.id,
        },
      ],
    };

    let getterReporte = getDeclaracionPDF;
    let args = id;
    if (ID) {
      getterReporte = postObtenerReporte;
      args = values;
    }

    const result = await getterReporte(args);
    if (result) {
      setBase64(result);
      setErrorPreviewer('');
    } else {
      setErrorPreviewer('No se pudo obtener el reporte');
    }
    setVisiblePreview(true);

    setLoading(false);
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue({
      ...record,
      contribuyente_vinculado: record.contribuyente_vinculado?.id,
    });
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const columns = [
    {
      titleText: 'No. de Declaración',
      dataIndex: 'numero_de_la_declaracion',
      key: 'numero_de_la_declaracion',
      width: 200,
    },
    {
      titleText: 'Fecha de Ultimo Estado',
      dataIndex: 'fecha_de_creacion',
      key: 'fecha_de_creacion',
      width: 200,
      render: (val) => (val ? toMoment(val)?.format(DATETIME_FRONTEND_FORMAT) : 'N/A'),
    },
    {
      titleText: 'Concepto',
      dataIndex: 'cargo',
      key: 'cargo',
      width: 400,
      render: (val) => (val?.descripcion || ''),
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estatus_de_declaracion_empresarial',
      key: 'estatus_de_declaracion_empresarial',
      width: 400,
      render: (val) => (estatus.find((item) => item.id === val)?.descripcion),
    },
    {
      titleText: 'Documento',
      dataIndex: 'id',
      key: 'id',
      width: 100,
      render: (_, values) => (
        <Button
          onClick={async () => {
            const tipoDePadron = getTipoDePadronPorContenType(values.content_type_padron);
            await onClickFormatoDeDeclaracion(values.id, tipoDePadron);
          }}
          icon={<EyeOutlined />}
        >
          Ver
        </Button>
      ),

    },
  ];

  const handleSearch = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const params = form.getFieldsValue();
      params.contribuyente = contribuyente;
      const declaraciones = await getDeclaracionesEmpresariales(params);
      setData(declaraciones);
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  return (
    <Row gutter={10}>
      <Spin tip="Cargando..." spinning={loading}>
        <Form
          layout="vertical"
          form={form}
          scrollToFirstError
          onFinish={() => handleSearch()}
        >
          <Row gutter={10}>
            <Col xs={24} sm={24} md={8}>
              <Form.Item
                name="tipo_de_declaracion_empresarial"
                label="Tipo de declaracion"
                hasFeedback
              >
                <Select
                  allowClear
                  dataSource={tiposDeDeclaracion}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={8}>
              <Form.Item
                name="ejercicio_anual"
                label="Ejercicio"
                hasFeedback
              >
                <Select allowClear valueProp="periodo" labelProp="periodo" dataSource={periodosFiscales} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={8}>
              <Button
                onClick={handleSearch}
                block
                style={{ marginTop: 21 }}
              >
                <SearchOutlined />
                <Typography.Text>
                  Buscar
                </Typography.Text>
              </Button>
            </Col>
          </Row>
        </Form>
        <Col span={24} />
        <Table
          cols={columns}
          data={data}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          noControls
          allowSort={false}
          allowSearch={false}
          rowKey="id"
          permission={permission}
          hasAllViewPermission
          mobileColIndex={0}
        />
      </Spin>
      <Previewer
        visible={visiblePreview}
        setVisible={setVisiblePreview}
        base64={base64}
        error={errorPreviewer}
      />
    </Row>
  );
};

DeclaracionesEmpresariales.propTypes = {
  contribuyente: PropTypes.number,
  permission: PropTypes.shape({
    add: PropTypes.bool.isRequired,
    change: PropTypes.bool.isRequired,
    delete: PropTypes.bool.isRequired,
    export: PropTypes.bool.isRequired,
    import: PropTypes.bool.isRequired,
    view: PropTypes.bool.isRequired,
  }).isRequired,
};

DeclaracionesEmpresariales.defaultProps = {
  contribuyente: null,
};

export default DeclaracionesEmpresariales;
